import { AlertColor, Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import Upload from '../../../../components/Upload/Upload';
import { FormDataModel } from '../../../../components/Form';
import Config from '../../../../utils/Config';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege, languages } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { AddEditLayoutForm } from './AddEditLayout/AddEditLayoutForm';

const LayoutList: FC = () => {
  const navigate = useNavigate();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const languageList = useSelector(languages) as { text: string; value: string }[];
  const userPrivileges = useSelector(Privilege) as string[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [layoutImage, setLayoutImage] = useState<any[]>([]);
  const [deletedImage, setDeletedImage] = useState<any[]>([]);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(() => {
    return (languageList || []).map((language, indexLang) => {
      const field = { ...AddEditLayoutForm(strings)[0] };
      field.field = `name_${language.value}`;
      field.autoFocus = indexLang === 0;
      field.label = `${field.label} (${language.text})`;
      field.responsive = { xs: 12 };
      return field;
    });
  }, [languageList]);

  const getList = useCallback(
    (filterData: PageProps) => {
      const body = filterData
        ? filterData
        : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
      getListData(setLoading, body, URLS.layout, setPageResponse);
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      setDeletedImage([]);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.get_edit_delete_layoutById(recordId));
          const container: FormDataModel = {};
          (languageList || []).forEach(language => {
            container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
          });
          setFormValues(container);
          const files: any[] = [];
          if (data.layoutImage) {
            files.push({
              preview: Config.BaseURL + data.layoutImage,
              name: data.fileName,
              id: data.id,
            });
          }
          setLayoutImage(files);
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        const container: FormDataModel = {};
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = '';
        });
        setFormValues(container);
        setLayoutImage([]);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
    setLayoutImage([]);
    setDeletedImage([]);
  };

  const uploadImage = async (layoutId: number) => {
    if ((!layoutImage || layoutImage.length === 0) && deletedImage.length === 0) {
      return;
    }
    const body = new FormData();
    layoutImage.forEach((file: any) => {
      body.append('layoutImage', file);
    });
    if (layoutImage.length === 0) {
      body.append('layoutImage', null as any);
    }
    body.append('deletedFile', deletedImage as any);
    await axiosInstance.put(URLS.layoutImage(layoutId), body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`name_${item.value}`],
      }));
      const payload = { localizedAttributes, maxCount: 1 };
      let layoutId = editingId;
      if (editingId) {
        await axiosInstance.put(URLS.get_edit_delete_layoutById(editingId), payload);
      } else {
        const { data } = await axiosInstance.post(URLS.addLayout, payload);
        layoutId = data?.id ?? data;
      }
      if (layoutId) {
        await uploadImage(layoutId);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.get_edit_delete_layoutById(editingId));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const getFileDetails = (value: any, _field: string, _error?: any, isDeleted?: any) => {
    if (Array.isArray(isDeleted) && isDeleted[0]?.name) {
      setDeletedImage(prev => [...prev, isDeleted[0].name]);
    }
    setLayoutImage(Array.isArray(value) ? value : []);
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.layout_title_list,
      subtitle: strings.layout_list_subtitle,
      searchField: 'layoutName',
      searchPlaceholder: 'Search layouts',
      AddButtonText: strings.layout_button_add,
      listApi: URLS.layout,
      deleteApi: URLS.get_edit_delete_layoutById,
      addPrivilege: privileges.add_layout,
      editPrivilege: privileges.edit_layout,
      deletePrivilege: privileges.delete_layout,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        {
          field: 'layoutImageUrl',
          headerName: '',
          flex: 1,
          image: true,
          imageInitialsField: 'layoutName',
        },
        {
          field: 'layoutName',
          headerName: strings.layout_column_name,
          flex: 4,
          sortable: true,
          localField: 'localizedAttributes.name',
        },
        usageTableColumn(row => [
          {
            count: row.venueConfigurationCount,
            singular: 'Config',
            plural: 'Configs',
            onClick: () => navigate(`${routes.venueConfiguration}?layoutId=${row.id}`),
          },
          { count: row.bookingCount, singular: 'Booking', plural: 'Bookings' },
        ]),
        lastUpdatedTableColumn(),
      ],
    }),
    [navigate, openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage
        isLoading={loading}
        isWhiteSpace={false}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getList}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.layout_title_edit : strings.layout_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_layout)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}>
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>Layout image</Typography>
          <Upload
            field="layoutImage"
            textChange={getFileDetails}
            inputValue={layoutImage}
            accept="image/*"
          />
        </Box>
      </MasterDataEntityDrawer>
    </>
  );
};

export default LayoutList;
